import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Controls,
  MiniMap,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import { Box, Button, Alert, CircularProgress, Typography, Tooltip } from "@mui/material";
import Add from "@mui/icons-material/Add";
import { useChatbotSlots, useChatbotMutations } from "../../hooks/useChatbot";
import { slotsToFlow } from "./chatbotFlowLayout";
import ChatbotNode from "./ChatbotNode";
import ChatbotNodePanel from "./ChatbotNodePanel";

const nodeTypes = { chatbot: ChatbotNode };

function ChatbotFlowEditorInner({ userId, onEmptyInit }) {
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const { data: slots = [], isLoading, refetch } = useChatbotSlots(userId);
  const { createSlot, updateSlot, deleteSlot } = useChatbotMutations(userId);
  const { fitView } = useReactFlow();
  /** Evita fitView a cada refetch (salvar): só enquadra na 1ª carga do fluxo por bot (userId). */
  const viewportFitRef = useRef({ userId: null, didFit: false });

  const { nodes: initialNodes, edges: initialEdges, slotMap } = useMemo(
    () => slotsToFlow(slots),
    [slots]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    if (viewportFitRef.current.userId !== userId) {
      viewportFitRef.current = { userId, didFit: false };
    }
  }, [userId]);

  useEffect(() => {
    if (initialNodes.length === 0) return;
    setNodes(initialNodes);
    setEdges(initialEdges);
    if (!viewportFitRef.current.didFit) {
      viewportFitRef.current.didFit = true;
      setTimeout(
        () =>
          fitView?.({
            padding: 0.12,
            minZoom: 0.4,
            maxZoom: 1.2,
          }),
        300
      );
    }
  }, [initialNodes, initialEdges, setNodes, setEdges, fitView]);

  const selectedSlot = selectedNodeId ? slotMap.get(selectedNodeId) ?? null : null;
  const isSelectedExcecao = selectedSlot?.identificador === "Exceção";

  const handleNodeClick = useCallback((_, node) => {
    setSelectedNodeId(node.id);
    setSaveError(null);
    setIsPanelOpen(true);
  }, []);

  const handleSave = useCallback(
    (payload) => {
      setSaveError(null);
      updateSlot.mutate(payload, {
        onSuccess: () => {
          const parentId = payload.id;
          const rawButtons = Array.isArray(payload.botoes)
            ? payload.botoes
            : String(payload.botoes || "")
                .split(",")
                .map((b) => b.trim())
                .filter(Boolean);

          const buttons = Array.from(new Set(rawButtons));

          if (!buttons.length) {
            refetch();
            setIsPanelOpen(false);
            return;
          }

          const children = slots.filter((s) => Number(s.id_pai) === Number(parentId));
          const hasChildForButton = (btn) =>
            children.some((child) => {
              const idStr = String(child.identificador || "");
              return idStr
                .split(",")
                .map((p) => p.trim())
                .filter(Boolean)
                .includes(btn);
            });

          const missingButtons = buttons.filter((btn) => !hasChildForButton(btn));

          if (!missingButtons.length) {
            refetch();
            setIsPanelOpen(false);
            return;
          }

          let created = 0;
          const total = missingButtons.length;

          missingButtons.forEach((btn) => {
            createSlot.mutate(
              {
                user_id: userId,
                parent_id: parentId,
                identificador: btn,
                criar_excecao: true,
                global: 0,
                ignorarglobal: 0,
              },
              {
                onSuccess: () => {
                  created += 1;
                  if (created === total) {
                    refetch();
                    setIsPanelOpen(false);
                  }
                },
                onError: (err) => {
                  setSaveError(err?.message || "Erro ao criar subpassos para os botões.");
                },
              }
            );
          });
        },
        onError: (err) => setSaveError(err?.message || "Erro ao salvar."),
      });
    },
    [updateSlot, refetch, slots, createSlot, userId]
  );

  const handleDelete = useCallback(
    (id) => {
      setSaveError(null);
      deleteSlot.mutate(id, {
        onSuccess: () => {
          refetch();
          setSelectedNodeId(null);
          setIsPanelOpen(false);
        },
        onError: (err) => setSaveError(err?.message || "Erro ao excluir."),
      });
    },
    [deleteSlot, refetch]
  );

  const handleAddStep = useCallback(
    (asChild = false) => {
      setSaveError(null);
      const parentId = asChild && selectedNodeId ? parseInt(selectedNodeId, 10) : 0;
      createSlot.mutate(
        {
          user_id: userId,
          parent_id: parentId,
          identificador: "Novo passo",
          criar_excecao: true,
          global: 0,
          ignorarglobal: 0,
        },
        {
          onSuccess: async (created) => {
            await refetch();
            const newId = created?.id != null ? String(created.id) : null;
            if (newId) {
              setSelectedNodeId(newId);
              setIsPanelOpen(true);
            }
          },
          onError: (err) => setSaveError(err?.message || "Erro ao criar passo."),
        }
      );
    },
    [userId, selectedNodeId, createSlot, refetch]
  );

  if (!userId) return null;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const hasSlots = slots.length > 0;

  return (
    <Box sx={{ width: "100%", flex: 1, minHeight: 420, position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          top: 12,
          left: 12,
          zIndex: 10,
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
          p: 1.5,
          borderRadius: 2,
          bgcolor: "background.paper",
          boxShadow: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {!hasSlots && onEmptyInit ? (
          <Button variant="contained" color="primary" size="small" startIcon={<Add />} onClick={onEmptyInit}>
            Inicializar fluxo (Início + Exceção)
          </Button>
        ) : (
          <>
            <Tooltip title="Selecione um passo no canvas e clique aqui para criar um subpasso ligado a ele.">
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleAddStep(true)}
                  disabled={!selectedNodeId || isSelectedExcecao}
                >
                  Adicionar subpasso
                </Button>
              </span>
            </Tooltip>
            {!selectedNodeId && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                Selecione um passo para adicionar subpasso
              </Typography>
            )}
            {selectedNodeId && isSelectedExcecao && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                O slot de Exceção não pode ter subpassos
              </Typography>
            )}
          </>
        )}
      </Box>

      {saveError && (
        <Alert severity="error" onClose={() => setSaveError(null)} sx={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}>
          {saveError}
        </Alert>
      )}

      {hasSlots ? (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          minZoom={0.25}
          maxZoom={1.5}
          defaultEdgeOptions={{
            type: "smoothstep",
            style: { strokeWidth: 2, stroke: "rgba(0,0,0,0.45)" },
            animated: false,
          }}
        >
          <Controls />
          <MiniMap />
          <Background color="#e0e0e0" gap={16} />
        </ReactFlow>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          bgcolor="action.hover"
          borderRadius={2}
        >
          <Typography color="text.secondary" gutterBottom>
            O fluxo tem um único passo raiz (Início). Inicialize para criar Início e Exceção; depois adicione subpassos a partir dos passos no canvas.
          </Typography>
          {onEmptyInit && (
            <Button variant="contained" startIcon={<Add />} onClick={onEmptyInit}>
              Inicializar fluxo (Início + Exceção)
            </Button>
          )}
        </Box>
      )}

      <ChatbotNodePanel
        slot={isPanelOpen ? selectedSlot : null}
        onClose={() => setIsPanelOpen(false)}
        onSave={handleSave}
        onDelete={handleDelete}
        onAddSubpasso={hasSlots && selectedNodeId && !isSelectedExcecao ? () => handleAddStep(true) : undefined}
        isSaving={updateSlot.isLoading}
        isDeleting={deleteSlot.isLoading}
        isAddingSubpasso={createSlot.isLoading}
        saveError={saveError}
        canDelete={!!selectedSlot && selectedSlot.identificador !== "Exceção" && selectedSlot.identificador !== "Início"}
      />
    </Box>
  );
}

export default function ChatbotFlowEditor({ userId, onEmptyInit }) {
  return (
    <ReactFlowProvider>
      <ChatbotFlowEditorInner userId={userId} onEmptyInit={onEmptyInit} />
    </ReactFlowProvider>
  );
}
